import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState, useEffect, useRef } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import {
  addNewInsuranceProvider,
  setClientInsurancePrimary,
  deleteClientInsurance,
  setClientInsurance,
  setClientInsurancePolicyEffectiveDate,
  setClientInsuranceExpirationDate,
  setClientInsuranceField,
} from "../../../redux/features/client-main/client-slice";
import { RootState, AppDispatch, useAppSelector } from "../../../redux/store";
import { InsuranceProviders } from "../../../types/client";
import DeleteIcon from "@mui/icons-material/Delete";
import { setActiveSteps } from "../../../redux/features/active-steps/active-slice";
import { InsuranceProvider } from "../../../types/response";

import { setUpdatedInsuranceProviders } from "../../../redux/features/client-main/client-insurance-slice";
import { getClientInsuranceProviders } from "../../../redux/features/client-main/client-insurance-slice";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { geocodeByAddress } from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { getAddressObject } from "../../../utils/AddressUtils";

interface Props {
  onSubmit?: () => void;
  deleteInsuranceProviderById?: (id: string) => Promise<void>;
  parent1Name?: string;
  parent2Name?: string;
}

const InsuranceDetails: React.FC<Props> = ({
  onSubmit,
  deleteInsuranceProviderById,
  parent1Name: parent1NameProp,
  parent2Name: parent2NameProp,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasErrorInsurance, setHasErrorInsurance] = useState<boolean>(false);
  const { activeStep } = useSelector((state: RootState) => state.activeStep);
  const { isEditClient } = useSelector((state: RootState) => state.client);
  const [hasError, setHasError] = useState<boolean>(false);
  const insuranceProviders = useSelector<RootState, InsuranceProvider[]>(
    (state) => state.clientInsuranceSlice.clientInsurance
  );
  const [inputValue, setInputValue] = useState<string>("");
  const [newInsuranceIndex, setNewInsuranceIndex] = useState<number>(0);
  const [inputShow, setInputShow] = useState<boolean>(false);
  const clientInsurances = useSelector<RootState, InsuranceProviders[]>(
    (state) => state.client.clientInsurance
  );
  const clientBasicDetails = useAppSelector(
    (state) => state.client.clientBasicDetails
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [isAddingSubscriber, setIsAddingSubscriber] = useState<boolean>(false);
  const [newSubscriberName, setNewSubscriberName] = useState<string>("");
  const [subscriberMenuOpen, setSubscriberMenuOpen] = useState<boolean>(false);
  const subscriberMenuInputRef = useRef<HTMLInputElement>(null);
  /* open state for relationship dropdown */
  const [relationshipMenuOpen, setRelationshipMenuOpen] =
    useState<boolean>(false);
  
  // Local states for address fields
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");

  const currentInsurance: InsuranceProviders =
    clientInsurances[selectedIndex] || {};

  const getSubscriberSelectValue = (): string => {
    if (!currentInsurance || !currentInsurance.subscriber) return "";
    if (currentInsurance.subscriber === effectiveParent1Name) return "parent1";
    if (currentInsurance.subscriber === effectiveParent2Name) return "parent2";
    return currentInsurance.subscriber;
  };

  // Sync local states with currentInsurance fields
  useEffect(() => {
    if (currentInsurance) {
      // Set address and addressSelectValue
      if (currentInsurance.address) {
        setAddress(currentInsurance.address);
      } else {
        setAddress("");
      }

      // Set other address fields
      setCity(currentInsurance.city || "");
      setState(currentInsurance.state || "");
      setZipCode(currentInsurance.zipCode || "");
    }
  }, [
    currentInsurance.address,
    currentInsurance.city,
    currentInsurance.state,
    currentInsurance.zipCode,
    selectedIndex
  ]);

  // dispatch for redux
  const dispatch = useDispatch<AppDispatch>();
  const dispatchAction = bindActionCreators(
    {
      getClientInsuranceProviders,
    },
    dispatch
  );
  const dispatchActionRef = useRef(dispatchAction);

  // #endregion

  // #region methods region

  //used for submit insurance details
  const handleSubmit = () => {
    setHasError(false);

    // Only validate the current insurance if it has a provider name
    if (currentInsurance && currentInsurance.insuranceProviderName) {
      // Check for required dates
      if (
        !currentInsurance.expirationDate ||
        !currentInsurance.policyEffectiveDate
      ) {
        setHasError(true);
        setErrorMessage("Expiration and Policy Effective Date are required");
        return;
      }
    }

    // Count primary insurances among valid ones (with provider name)
    const validInsurances = clientInsurances.filter(
      (item) => item.insuranceProviderName
    );

    const primaryCount = validInsurances.filter(
      (item) => item.isPrimary
    ).length;

    if (validInsurances.length > 0 && primaryCount === 0) {
      setHasError(true);
      setErrorMessage("At least one provider should be primary");
      return;
    } else if (primaryCount > 1) {
      setHasError(true);
      setErrorMessage("Only one insurance provider can be primary");
      return;
    } else {
      if (isEditClient) {
        if (onSubmit) {
          onSubmit();
        }
      } else {
        dispatch(setActiveSteps(activeStep + 1));
      }
    }
  };

  const handleAddNewInsurance = (): void => {
    let count: number = 0;
    while (count === 0) {
      for (let i = 0; i < clientInsurances.length; i++) {
        if (!clientInsurances[i].insuranceId) {
          count = 1;
        }
      }
      break;
    }
    if (count === 0) {
      if (hasError) {
        setHasError(false);
      }
      dispatch(addNewInsuranceProvider({}));
      setSelectedIndex(clientInsurances.length);
    } else {
      setHasError(true);
      setErrorMessage("Please select Insurance Provider");
      setTimeout(() => {
        setHasError(false);
        setErrorMessage("");
      }, 2000);
    }
  };

  const removeFocus = (id: string) => {
    document.getElementById(id)?.blur();
  };

  const handleAddressSelect = (selected: any) => {
    if (!selected) {
      // Clear local states
      setAddress("");
      setCity("");
      setState("");
      setZipCode("");
      
      // Clear Redux state
      dispatch(
        setClientInsuranceField({
          index: selectedIndex,
          name: "address",
          value: "",
        })
      );
      dispatch(
        setClientInsuranceField({
          index: selectedIndex,
          name: "city",
          value: "",
        })
      );
      dispatch(
        setClientInsuranceField({
          index: selectedIndex,
          name: "state",
          value: "",
        })
      );
      dispatch(
        setClientInsuranceField({
          index: selectedIndex,
          name: "zipCode",
          value: "",
        })
      );
      return;
    }
    
    const description = selected?.label;
    if (!description) return;

    // Set full address locally
    setAddress(description);

    geocodeByAddress(description).then(
      async (results: google.maps.GeocoderResult[]) => {
        const addressObject = await getAddressObject(
          results[0].address_components
        );

        // Update local states
        setCity(addressObject?.city || "");
        setState(addressObject?.province || "");
        setZipCode(addressObject?.postal_code || "");

        // Set address full in Redux
        dispatch(
          setClientInsuranceField({
            index: selectedIndex,
            name: "address",
            value: description,
          })
        );

        // Set City Field in Redux
        dispatch(
          setClientInsuranceField({
            index: selectedIndex,
            name: "city",
            value: addressObject?.city || "",
          })
        );

        // Set State Field in Redux
        dispatch(
          setClientInsuranceField({
            index: selectedIndex,
            name: "state",
            value: addressObject?.province || "",
          })
        );

        // Set ZipCode Field in Redux
        dispatch(
          setClientInsuranceField({
            index: selectedIndex,
            name: "zipCode",
            value: addressObject?.postal_code || "",
          })
        );
      }
    );
  };

  useEffect(() => {
    dispatchActionRef.current.getClientInsuranceProviders();
  }, []);

  const storeParent1Name =
    clientBasicDetails.parentFirstName && clientBasicDetails.parentLastName
      ? `${clientBasicDetails.parentFirstName} ${clientBasicDetails.parentLastName}`
      : "";
  const storeParent2Name =
    clientBasicDetails.parent2FirstName && clientBasicDetails.parent2LastName
      ? `${clientBasicDetails.parent2FirstName} ${clientBasicDetails.parent2LastName}`
      : "";

  const effectiveParent1Name = parent1NameProp ?? storeParent1Name;
  const effectiveParent2Name = parent2NameProp ?? storeParent2Name;

  return (
    <Box className="stepperForm insurance-dialog">
      <ValidatorForm onSubmit={handleSubmit}>
        <div className="stepperForm__inner">
          <Grid container spacing={2} className="InsuranceHeader">
            <Grid size={{ xs: 12, lg: 6 }}>
              <h1 className="stepperForm__title">Insurance Information</h1>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }} className="InsuranceHeader__btn">
              <Button
                onClick={() => {
                  handleAddNewInsurance();
                }}
              >
                + Add Client Insurance
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            {/* Left sidebar - Insurance list */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={1}
                sx={{ height: "100%", maxHeight: "600px", overflow: "auto" }}
              >
                <List>
                  {clientInsurances.map((insurance, index) => (
                    <React.Fragment key={index}>
                      <ListItemButton
                        selected={selectedIndex === index}
                        onClick={() => setSelectedIndex(index)}
                        sx={{
                          backgroundColor:
                            selectedIndex === index ? "#f5f5f5" : "inherit",
                          borderLeft:
                            selectedIndex === index
                              ? "4px solid #1976d2"
                              : "4px solid transparent",
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="subtitle1">
                                {insurance.insuranceProviderName ||
                                  "New Insurance"}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            insurance.policyEffectiveDate
                              ? `Until: ${dayjs(
                                  insurance.policyEffectiveDate
                                ).format("MM/DD/YYYY")}`
                              : "No date"
                          }
                        />
                        <DeleteIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            if (insurance.id && deleteInsuranceProviderById) {
                              deleteInsuranceProviderById(insurance.id);
                            } else {
                              dispatch(
                                deleteClientInsurance({
                                  index: index,
                                })
                              );
                            }
                            const newLength = clientInsurances.length - 1;
                            if (newLength <= 0) {
                              setSelectedIndex(0);
                            } else if (index <= selectedIndex) {
                              setSelectedIndex(Math.max(0, selectedIndex - 1));
                            }
                          }}
                          fontSize="small"
                          sx={{
                            marginTop: "2px",
                            marginLeft: "auto",
                            cursor: "pointer",
                            color: "#096DD9",
                          }}
                        />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Right form panel */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                {clientInsurances.length > 0 && (
                  <Grid container spacing={2}>
                    {/* Insurance Provider */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Insurance Provider
                        </InputLabel>
                        <FormControl fullWidth size="small">
                          <Select
                            value={
                              insuranceProviders.find(
                                (inc) =>
                                  inc.insuranceProviderName ===
                                  currentInsurance.insuranceProviderName
                              )?.id || ""
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                removeFocus("clientInsurance");
                              }
                            }}
                            onChange={(e: SelectChangeEvent<string>) => {
                              const item: InsuranceProvider | undefined =
                                insuranceProviders.find(
                                  (item: InsuranceProvider) =>
                                    item.id === e.target.value
                                );

                              setHasError(false);
                              if (item) {
                                const clientInsuranceId:
                                  | InsuranceProviders
                                  | undefined = clientInsurances.find(
                                  (val) => val.insuranceId === e.target.value
                                );

                                if (!clientInsuranceId) {
                                  dispatch(
                                    setClientInsurance({
                                      index: selectedIndex,
                                      insuranceId: item.id,
                                      insuranceProviderName:
                                        item.insuranceProviderName,
                                    })
                                  );
                                  /* --------------------------------------------------
                                   * Always overwrite client-insurance address fields
                                   * with metadata from the selected provider
                                   * -------------------------------------------------- */
                                  const meta = item.data ?? {};
                                  const autoFields: any = {
                                    payerId: meta.payerId ?? "",
                                    address: meta.fullAddress ?? "",
                                    city: meta.city ?? "",
                                    state: meta.state ?? "",
                                    zipCode: meta.zipCode ?? "",
                                  };

                                  // Update local component state
                                  setAddress(autoFields.address || "");
                                  setCity(autoFields.city || "");
                                  setState(autoFields.state || "");
                                  setZipCode(autoFields.zipCode || "");

                                  // Overwrite Redux state with provider metadata
                                  (
                                    Object.keys(autoFields) as Array<
                                      keyof typeof autoFields
                                    >
                                  ).forEach((fieldName) => {
                                    dispatch(
                                      setClientInsuranceField({
                                        index: selectedIndex,
                                        name: fieldName as string,
                                        value: autoFields[fieldName],
                                      })
                                    );
                                  });
                                } else {
                                  setHasErrorInsurance(true);
                                }
                              } else {
                                // Clear fields if no provider is selected
                                dispatch(
                                  setClientInsurance({
                                    index: selectedIndex,
                                    insuranceId: "",
                                    insuranceProviderName: "",
                                  })
                                );
                                
                                // Clear local states
                                setAddress("");
                                setCity("");
                                setState("");
                                setZipCode("");
                                
                                // Clear Redux state
                                ["address", "city", "state", "zipCode"].forEach(
                                  (fieldName) => {
                                    dispatch(
                                      setClientInsuranceField({
                                        index: selectedIndex,
                                        name: fieldName,
                                        value: "",
                                      })
                                    );
                                  }
                                );
                              }
                            }}
                          >
                            {insuranceProviders.map(
                              (item: InsuranceProvider) => (
                                <MenuItem key={item.id} value={item.id}>
                                  {item.insuranceProviderName}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    {/* Payer ID */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">Payer ID</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={currentInsurance.payerId || ""}
                          onChange={(e) => {
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "payerId",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Effective Date */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Effective Date
                        </InputLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={
                              currentInsurance?.policyEffectiveDate
                                ? dayjs(currentInsurance.policyEffectiveDate)
                                : null
                            }
                            onChange={(e) => {
                              setHasError(false);
                              dispatch(
                                setClientInsurancePolicyEffectiveDate({
                                  index: selectedIndex,
                                  policyEffectiveDate: e?.format("YYYY-MM-DD"),
                                })
                              );
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                placeholder: "MM/DD/YYYY",
                                error: false,
                                InputLabelProps: { shrink: false },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Box>
                    </Grid>

                    {/* Expiration Date */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Expiration Date
                        </InputLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={
                              currentInsurance?.expirationDate
                                ? dayjs(currentInsurance.expirationDate)
                                : null
                            }
                            onChange={(e) => {
                              setHasError(false);
                              setErrorMessage("");
                              dispatch(
                                setClientInsuranceExpirationDate({
                                  index: selectedIndex,
                                  expirationDate: e?.format("YYYY-MM-DD"),
                                })
                              );
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                placeholder: "MM/DD/YYYY",
                                error: false,
                                InputLabelProps: { shrink: false },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Box>
                    </Grid>

                    {/* Primary/Secondary Checkboxes */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        className="fromGroup"
                        sx={{ display: "flex", gap: 2 }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={currentInsurance.isPrimary || false}
                              onChange={() => {
                                setHasError(false);
                                setErrorMessage("");
                                dispatch(
                                  setClientInsurancePrimary({
                                    index: selectedIndex,
                                  })
                                );
                              }}
                            />
                          }
                          label="Primary"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!currentInsurance.isPrimary}
                              onChange={() => {
                                setHasError(false);
                                dispatch(
                                  setClientInsurancePrimary({
                                    index: selectedIndex,
                                  })
                                );
                              }}
                            />
                          }
                          label="Secondary"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6">
                        Subscriber Information
                      </Typography>
                    </Grid>

                    {/* Subscriber Dropdown */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Subscriber
                        </InputLabel>
                        <FormControl fullWidth size="small">
                          <Select
                            value={getSubscriberSelectValue()}
                            sx={{
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected)
                                return (
                                  <Box
                                    component="span"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    Select Subscriber
                                  </Box>
                                );
                              return currentInsurance.subscriber || "";
                            }}
                            open={subscriberMenuOpen}
                            onOpen={() => setSubscriberMenuOpen(true)}
                            onClose={() => {
                              setIsAddingSubscriber(false);
                              setNewSubscriberName("");
                              setSubscriberMenuOpen(false);
                            }}
                            MenuProps={{
                              disableAutoFocusItem: true,
                              MenuListProps: {
                                onKeyDown: (e: any) => {
                                  if (isAddingSubscriber) e.stopPropagation();
                                },
                              },
                            }}
                            onChange={(e) => {
                              const value = e.target.value as string;
                              if (value === "parent1" && effectiveParent1Name) {
                                dispatch(
                                  setClientInsuranceField({
                                    index: selectedIndex,
                                    name: "subscriber",
                                    value: effectiveParent1Name,
                                  })
                                );
                                setSubscriberMenuOpen(false);
                              } else if (
                                value === "parent2" &&
                                effectiveParent2Name
                              ) {
                                dispatch(
                                  setClientInsuranceField({
                                    index: selectedIndex,
                                    name: "subscriber",
                                    value: effectiveParent2Name,
                                  })
                                );
                                setSubscriberMenuOpen(false);
                              } else if (value === "") {
                                dispatch(
                                  setClientInsuranceField({
                                    index: selectedIndex,
                                    name: "subscriber",
                                    value: "",
                                  })
                                );
                                setSubscriberMenuOpen(false);
                              } else if (value) {
                                dispatch(
                                  setClientInsuranceField({
                                    index: selectedIndex,
                                    name: "subscriber",
                                    value: value,
                                  })
                                );
                                setSubscriberMenuOpen(false);
                              }
                            }}
                          >
                            {isAddingSubscriber ? (
                              <MenuItem
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <TextField
                                  autoFocus
                                  inputRef={subscriberMenuInputRef}
                                  size="small"
                                  placeholder="Type and press Enter"
                                  value={newSubscriberName}
                                  onChange={(e) =>
                                    setNewSubscriberName(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === "Enter") {
                                      const trimmedName =
                                        newSubscriberName.trim();
                                      if (trimmedName) {
                                        dispatch(
                                          setClientInsuranceField({
                                            index: selectedIndex,
                                            name: "subscriber",
                                            value: trimmedName,
                                          })
                                        );
                                        setIsAddingSubscriber(false);
                                        setNewSubscriberName("");
                                        setSubscriberMenuOpen(false);
                                      }
                                    } else if (e.key === "Tab") {
                                      const trimmedName =
                                        newSubscriberName.trim();
                                      if (trimmedName) {
                                        dispatch(
                                          setClientInsuranceField({
                                            index: selectedIndex,
                                            name: "subscriber",
                                            value: trimmedName,
                                          })
                                        );
                                      }
                                      setIsAddingSubscriber(false);
                                      setNewSubscriberName("");
                                      setSubscriberMenuOpen(false);
                                    } else if (e.key === "Escape") {
                                      setIsAddingSubscriber(false);
                                      setNewSubscriberName("");
                                      setSubscriberMenuOpen(false);
                                    }
                                  }}
                                  onBlur={() => {
                                    setIsAddingSubscriber(false);
                                    setNewSubscriberName("");
                                    setSubscriberMenuOpen(false);
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsAddingSubscriber(true);
                                  setSubscriberMenuOpen(true);
                                  requestAnimationFrame(() =>
                                    subscriberMenuInputRef.current?.focus()
                                  );
                                }}
                              >
                                <ListItemText
                                  primary="+ Add Subscriber Name"
                                  primaryTypographyProps={{ color: "primary" }}
                                />{" "}
                              </MenuItem>
                            )}
                            {effectiveParent1Name && (
                              <MenuItem
                                value="parent1"
                                onClick={() => {
                                  if (
                                    getSubscriberSelectValue() === "parent1"
                                  ) {
                                    dispatch(
                                      setClientInsuranceField({
                                        index: selectedIndex,
                                        name: "subscriber",
                                        value: "",
                                      })
                                    );
                                    setSubscriberMenuOpen(false);
                                  }
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={
                                    getSubscriberSelectValue() === "parent1"
                                  }
                                />
                                <ListItemText primary={effectiveParent1Name} />
                              </MenuItem>
                            )}
                            {effectiveParent2Name && (
                              <MenuItem
                                value="parent2"
                                onClick={() => {
                                  if (
                                    getSubscriberSelectValue() === "parent2"
                                  ) {
                                    dispatch(
                                      setClientInsuranceField({
                                        index: selectedIndex,
                                        name: "subscriber",
                                        value: "",
                                      })
                                    );
                                    setSubscriberMenuOpen(false);
                                  }
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={
                                    getSubscriberSelectValue() === "parent2"
                                  }
                                />
                                <ListItemText primary={effectiveParent2Name} />
                              </MenuItem>
                            )}
                            {/* Show current subscriber if it's a custom value */}
                            {currentInsurance.subscriber &&
                              currentInsurance.subscriber !==
                                effectiveParent1Name &&
                              currentInsurance.subscriber !==
                                effectiveParent2Name && (
                                <MenuItem value={currentInsurance.subscriber}>
                                  {/* Toggle off if already selected */}
                                  <Box
                                    sx={{ display: "contents" }}
                                    onClick={() => {
                                      if (
                                        getSubscriberSelectValue() ===
                                        currentInsurance.subscriber
                                      ) {
                                        dispatch(
                                          setClientInsuranceField({
                                            index: selectedIndex,
                                            name: "subscriber",
                                            value: "",
                                          })
                                        );
                                        setSubscriberMenuOpen(false);
                                      }
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        getSubscriberSelectValue() ===
                                        currentInsurance.subscriber
                                      }
                                    />
                                    <ListItemText
                                      primary={currentInsurance.subscriber}
                                    />
                                  </Box>
                                </MenuItem>
                              )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    {/* Subscriber DOB */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Subscriber's DOB
                        </InputLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={
                              currentInsurance?.subscriberDOB
                                ? dayjs(currentInsurance.subscriberDOB)
                                : null
                            }
                            onChange={(e) => {
                              dispatch(
                                setClientInsuranceField({
                                  index: selectedIndex,
                                  name: "subscriberDOB",
                                  value: e?.format("YYYY-MM-DD"),
                                })
                              );
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                placeholder: "MM/DD/YYYY",
                                error: false,
                                InputLabelProps: { shrink: false },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Box>
                    </Grid>

                    {/* Subscriber Relationship */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">
                          Subscriber's Relationship
                        </InputLabel>
                        <FormControl fullWidth size="small">
                          <Select
                            value={
                              currentInsurance.subscriberRelationship || ""
                            }
                            sx={{
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected)
                                return (
                                  <Box
                                    component="span"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    Select Relationship
                                  </Box>
                                );
                              /* selected is a simple string */
                              return selected as string;
                            }}
                            open={relationshipMenuOpen}
                            onOpen={() => setRelationshipMenuOpen(true)}
                            onClose={() => setRelationshipMenuOpen(false)}
                            onChange={(e) => {
                              const val = e.target.value as string;
                              const current =
                                currentInsurance.subscriberRelationship || "";
                              const next = val === current ? "" : val;
                              dispatch(
                                setClientInsuranceField({
                                  index: selectedIndex,
                                  name: "subscriberRelationship",
                                  value: next,
                                })
                              );
                            }}
                          >
                            <MenuItem
                              value="Self"
                              onMouseDown={(e) => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Self"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={() => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Self"
                                ) {
                                  dispatch(
                                    setClientInsuranceField({
                                      index: selectedIndex,
                                      name: "subscriberRelationship",
                                      value: "",
                                    })
                                  );
                                  setRelationshipMenuOpen(false);
                                }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Self"
                                }
                              />
                              <ListItemText primary="Self" />
                            </MenuItem>
                            <MenuItem
                              value="Mother"
                              onMouseDown={(e) => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Mother"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={() => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Mother"
                                ) {
                                  dispatch(
                                    setClientInsuranceField({
                                      index: selectedIndex,
                                      name: "subscriberRelationship",
                                      value: "",
                                    })
                                  );
                                  setRelationshipMenuOpen(false);
                                }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Mother"
                                }
                              />
                              <ListItemText primary="Mother" />
                            </MenuItem>
                            <MenuItem
                              value="Father"
                              onMouseDown={(e) => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Father"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={() => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Father"
                                ) {
                                  dispatch(
                                    setClientInsuranceField({
                                      index: selectedIndex,
                                      name: "subscriberRelationship",
                                      value: "",
                                    })
                                  );
                                  setRelationshipMenuOpen(false);
                                }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Father"
                                }
                              />
                              <ListItemText primary="Father" />
                            </MenuItem>
                            <MenuItem
                              value="Guardian"
                              onMouseDown={(e) => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Guardian"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={() => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Guardian"
                                ) {
                                  dispatch(
                                    setClientInsuranceField({
                                      index: selectedIndex,
                                      name: "subscriberRelationship",
                                      value: "",
                                    })
                                  );
                                  setRelationshipMenuOpen(false);
                                }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Guardian"
                                }
                              />
                              <ListItemText primary="Guardian" />
                            </MenuItem>
                            <MenuItem
                              value="Other"
                              onMouseDown={(e) => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Other"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={() => {
                                if (
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Other"
                                ) {
                                  dispatch(
                                    setClientInsuranceField({
                                      index: selectedIndex,
                                      name: "subscriberRelationship",
                                      value: "",
                                    })
                                  );
                                  setRelationshipMenuOpen(false);
                                }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  (currentInsurance.subscriberRelationship ||
                                    "") === "Other"
                                }
                              />
                              <ListItemText primary="Other" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    {/* Group No */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">Group No</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={currentInsurance.groupNo || ""}
                          onChange={(e) => {
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "groupNo",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Member ID */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">Member ID</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={currentInsurance.memberId || ""}
                          onChange={(e) => {
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "memberId",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6">Address Information</Typography>
                    </Grid>

                    {/* Address Autocomplete */}
                    <Grid size={{ xs: 12 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">Address</InputLabel>
                        <GooglePlacesAutocomplete
                          key={address || ""}
                          apiKey={process.env.React_APP_GOOGLE_AUTOCOMPLETE_KEY}
                          selectProps={{
                            defaultInputValue: address?.trimStart() || "",
                            setValue: address.trimStart(),
                            onChange: (selected: any) => {
                              handleAddressSelect(selected);
                            },
                            debounce: 300,
                            styles: {
                              input: (provided: any) => ({
                                ...provided,
                                fontSize: 16,
                              }),
                              option: (provided: any) => ({
                                ...provided,
                                fontSize: 16,
                              }),
                              singleValue: (provided: any) => ({
                                ...provided,
                                fontSize: 16,
                              }),
                            },
                          }}
                          autocompletionRequest={{
                            componentRestrictions: {
                              country: ["us", "ca"],
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* City */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">City</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={city}
                          onChange={(e) => {
                            setCity(e.target.value);
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "city",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* State */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">State</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={state}
                          onChange={(e) => {
                            setState(e.target.value);
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "state",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Zip Code */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box className="fromGroup">
                        <InputLabel className="fromLabel">Zip Code</InputLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={zipCode}
                          onChange={(e) => {
                            setZipCode(e.target.value);
                            dispatch(
                              setClientInsuranceField({
                                index: selectedIndex,
                                name: "zipCode",
                                value: e.target.value,
                              })
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>

          {hasErrorInsurance && (
            <FormHelperText style={{ color: "#d32f2f", marginTop: "8px" }}>
              "You have already added Insurance Provider please choice another
              one"
            </FormHelperText>
          )}

          {/* handle custom input for insurance provider */}
          {inputShow && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  autoFocus={true}
                  type="text"
                  placeholder={"Enter new insurance & hit enter key"}
                  value={inputValue}
                  id="input"
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === "Enter" && inputValue?.length > 0) {
                      document.getElementById("input")?.blur();

                      const newInsuranceList: InsuranceProvider[] = [
                        ...insuranceProviders,
                      ];
                      if (inputValue.trim().length > 0) {
                        const newInsuranceProvider: InsuranceProvider = {
                          id: `${newInsuranceIndex}`,
                          insuranceProviderName: inputValue,
                        };
                        newInsuranceList.push(newInsuranceProvider);
                        dispatch(
                          setUpdatedInsuranceProviders(newInsuranceList)
                        );

                        dispatch(
                          setClientInsurance({
                            index: newInsuranceIndex,
                            insuranceId: `${newInsuranceIndex}`,
                            insuranceProviderName: inputValue,
                          })
                        );
                        setInputShow(false);
                        setNewInsuranceIndex(newInsuranceIndex + 1);
                        setInputValue("");
                        setHasError(false);
                      }
                    }
                  }}
                  onChange={(
                    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                  ) => {
                    if (!(e.target.value[0] === " ")) {
                      setInputValue(e.target.value);
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}

          {hasError && !inputValue && (
            <FormHelperText style={{ color: "#d32f2f", marginTop: "8px" }}>
              {errorMessage}
            </FormHelperText>
          )}
        </div>
        <Box className="stepperForm__footer">
          {!isEditClient ? (
            <Button
              type="submit"
              color="inherit"
              disabled={activeStep === 0 ? true : false}
              className="border-button"
              onClick={() => dispatch(setActiveSteps(activeStep - 1))}
            >
              Previous
            </Button>
          ) : (
            ""
          )}

          <Box sx={{ flex: "1 1 auto" }} />

          <Button
            type="submit"
            className="button"
            onClick={() => setHasError(true)}
          >
            {isEditClient ? "Save Changes" : "Next"}
          </Button>
        </Box>
      </ValidatorForm>
    </Box>
  );
};

export default InsuranceDetails;
